import React, { useEffect, useRef } from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { ResponsiveValues } from '@sketch/global-styles'

import {
  useFlag,
  routes,
  useUserProfile,
  useFolderTerm,
  isProjectRoute,
  isDraftsRoute,
} from '@sketch/modules-common'

import { Breakpoint, pluralize } from '@sketch/components'
import EmptyState from '../EmptyStateOld'

import {
  DocumentGridItemPlaceholder,
  DocumentTableItemPlaceholder,
  RenderSharesDropdown,
} from '../DocumentItem'

import {
  GetStorageItemsQueryVariables,
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'

import {
  ActionTableCell,
  Footer,
  HeaderTableCell,
  FocusLink,
} from './DocumentItemsLayout.styles'
import {
  DraggableShareItem,
  DraggableShareItemProps,
} from '../DraggableShareItem'
import { useDropShareToNestedProject } from '../../hooks/useDropShareToNestedProject'
import { useDropShareToCollection } from '../../hooks/useDropShareToCollection'
import { useStorageItemsFilters } from '../../../storageItems/hooks'
import { useSortingSettings } from '../../hooks/useSortSettings'
import { parseFilters } from '../../../projects/hooks/useProject'
import { StorageItemsLayout } from '../../../storageItems/components'

interface CustomColumns {
  customColumns?: ResponsiveValues<number>
}

interface DocumentItemsLayoutProps<S> extends CustomColumns {
  workspaceId: string
  shares: S[]
  showProjectName: boolean
  showCollectionName?: boolean
  onLoadMore: () => Promise<any>
  totalCount: number
  renderDropdown: RenderSharesDropdown<S>
  action?: (share: S) => (() => void) | 'link'
  search?: string
  canUseDesignSystemManager?: boolean
  projectId?: string
  /** In some places, dragging is allowed but dropping is not allowed */
  canDropShares?: boolean
}

export const DocumentItemsLayout = <S extends StorageItemFragment>(
  props: DocumentItemsLayoutProps<S>
) => {
  const {
    workspaceId,
    shares,
    showProjectName,
    onLoadMore,
    action: actionCreator = () => 'link',
    totalCount,
    renderDropdown,
    search,
    customColumns,
    projectId,
    showCollectionName,
    canDropShares,
  }: typeof props = props

  const history = useHistory()

  const isDesignSystemsOn = useFlag('design-systems')
  const isNestedProjectsOn = useFlag('nested-projects')

  // Route match
  const { path } = useRouteMatch()
  const isProjectView = isProjectRoute(path)
  const isDraftView = isDraftsRoute(path)
  const isValidViewToCreateProject = isProjectView || isDraftView

  const isWorkspaceLibrariesRoute = useRouteMatch({
    path: routes.WORKSPACE_LIBRARIES.template(),
    exact: true,
  })

  // Make sure the onLoadMore Reference is always the latest
  const onLoadMoreRef = useRef<() => Promise<any>>(onLoadMore)
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  // Get the User Identifier to show the "You" label
  const { data: userData } = useUserProfile()
  const userIdentifier = userData?.me.identifier

  const folderTerm = useFolderTerm()

  const { sort } = useSortingSettings()
  const { filters } = useStorageItemsFilters()

  // Drag and Drop
  const storageItemsQueryVariables = {
    projectIdentifier: projectId || '',
    search,
    ...parseFilters(filters, search),
    after: null,
    sharesSortOrder: sort,
  } satisfies GetStorageItemsQueryVariables

  const dropShareToNestedProject = useDropShareToNestedProject({
    projectIdentifier: projectId,
    storageItemsQueryVariables,
  })
  const dropShareToCollection = useDropShareToCollection({
    projectIdentifier: projectId,
  })

  const renderGridItem = (item: S, isMobile: boolean) => {
    const action = actionCreator(item)

    const userCanUpdateSettings =
      item.__typename === 'Share' && item.userCanUpdateSettings

    const commonDraggableShareItemProps: DraggableShareItemProps<StorageItemFragment> =
      {
        presentation: 'grid',
        workspaceIdentifier: workspaceId,
        item,
        // TODO: Fix this type
        // https://linear.app/sketch/issue/SWEB-1363
        renderDropdown: renderDropdown as any,
        showProjectName,
        showCollectionName: Boolean(showCollectionName),
        draggable: !isMobile && userCanUpdateSettings,
        onShareDropped: isNestedProjectsOn
          ? isValidViewToCreateProject
            ? dropShareToNestedProject
            : undefined
          : dropShareToCollection,
      }

    if (action !== 'link') {
      return (
        <DraggableShareItem
          {...commonDraggableShareItemProps}
          onClick={() => action()}
        />
      )
    }

    return (
      <FocusLink
        draggable={false}
        key={item.identifier}
        to={{
          pathname: routes.SHARE_VIEW.create({ shareID: item.identifier }),
          state: { componentsView: true },
        }}
      >
        <DraggableShareItem
          {...commonDraggableShareItemProps}
          userIdentifier={userIdentifier}
        />
      </FocusLink>
    )
  }

  const handleListItemClick = (
    event: React.MouseEvent<HTMLElement>,
    item: StorageItemFragment
  ) => {
    const link = routes.SHARE_VIEW.create({
      shareID: item.identifier,
    })

    if (event.metaKey) {
      /* Simulate the link opening with meta key */
      window.open(link, '_blank')
    } else {
      history.push(link)
    }
  }

  const renderListItem = (item: S, isMobile: boolean) => {
    const action = actionCreator(item)

    const userCanUpdateSettings =
      item.__typename === 'Share' && item.userCanUpdateSettings

    return (
      <DraggableShareItem
        presentation="list"
        workspaceIdentifier={workspaceId}
        userIdentifier={userIdentifier}
        item={item}
        renderDropdown={renderDropdown}
        showProjectName={showProjectName}
        showCollectionName={showCollectionName}
        draggable={!isMobile && userCanUpdateSettings}
        droppable={canDropShares}
        onShareDropped={
          isNestedProjectsOn
            ? isValidViewToCreateProject
              ? dropShareToNestedProject
              : undefined
            : dropShareToCollection
        }
        onClick={event =>
          action === 'link' ? handleListItemClick(event, item) : action()
        }
      />
    )
  }

  const renderLayout = (
    isMobile: boolean,
    customColumns?: ResponsiveValues<number>
  ) => {
    // for design systems search: check if we're on the library route and if empty count for a special message
    if (isWorkspaceLibrariesRoute && totalCount === 0 && isDesignSystemsOn) {
      return (
        <EmptyState
          search={search}
          title="No Libraries"
          description="No Libraries added yet."
          icon="link"
        />
      )
    }

    const commontStorageItemsLayoutProps = {
      items: shares,
      onLoadMore: onLoadMore,
      customColumns: customColumns,
      totalCount: totalCount,
    }

    if (documentsLayout === 'LIST') {
      const headerLabel = pluralize('Document', 'Documents', totalCount)
      const headerColumns = [
        {
          label: `${totalCount} ${headerLabel}`,
          customCell: HeaderTableCell,
        },
        ...(showCollectionName
          ? [{ label: 'Collection', customCell: HeaderTableCell }]
          : [{ label: folderTerm.upper, customCell: HeaderTableCell }]),
        { label: 'Last Updated', customCell: HeaderTableCell },
        { label: 'Updated By', customCell: HeaderTableCell },
        { label: '', customCell: ActionTableCell },
      ]

      return (
        <StorageItemsLayout
          header={headerColumns}
          Skeleton={props => (
            <DocumentTableItemPlaceholder
              columnCount={headerColumns.length}
              {...props}
            />
          )}
          renderItem={item => renderListItem(item, isMobile)}
          {...commontStorageItemsLayoutProps}
        />
      )
    } else {
      return (
        <StorageItemsLayout
          Skeleton={DocumentGridItemPlaceholder}
          renderItem={item => renderGridItem(item, isMobile)}
          {...commontStorageItemsLayoutProps}
        />
      )
    }
  }

  return (
    <>
      <Breakpoint on="sm">
        {matches => renderLayout(!matches, customColumns)}
      </Breakpoint>
      <Footer />
    </>
  )
}
